import avatar from "@/images/avatar.webp"
import classNames from "classnames"
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { useAppSelector } from "@/store/hooks"
import { getUserCreds, host } from "@/utils/utils"
import {
  clampScore,
  formatNumber,
  formatPercent,
  getNextTier,
  getRankTier,
} from "../Stats/statsUi"

// Rungs of the global ladder the lobby shows. The ladder is short by nature —
// it sits at the bottom of the column and takes only the room it needs.
const LADDER_SIZE = 8

const medalColor = (index: number) =>
  ["#FBBF24", "#CBD5E1", "#D97706"][index] ?? "rgba(255,255,255,0.3)"

const ordinal = (place: number) => {
  const remainder = place % 100
  if (remainder >= 11 && remainder <= 13) return `${place}th`
  return `${place}${["th", "st", "nd", "rd"][place % 10] ?? "th"}`
}

// "3h ago" beats a date on a row this small — the only thing a player wants
// from it here is whether this was today or a while back.
const timeAgo = (value: string) => {
  const then = new Date(value).getTime()
  if (isNaN(then)) return ""
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000))
  if (minutes < 1) return "just now"
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  if (days < 30) return `${days}d ago`
  return `${Math.round(days / 30)}mo ago`
}

const Card: React.FC<{ className?: string; children: React.ReactNode }> = ({
  className,
  children,
}) => (
  <div
    className={classNames(
      "flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-black/40 shadow-[0_8px_40px_rgba(0,0,0,0.35)] backdrop-blur-md",
      className
    )}
  >
    {children}
  </div>
)

// Same header strip the open-games panel uses, so the two columns read as one
// set rather than two unrelated widgets.
const CardHeading: React.FC<{
  label: string
  icon: string
  action?: React.ReactNode
}> = ({ label, icon, action }) => (
  <div className="flex items-center justify-between gap-3 border-b border-white/10 px-5 py-3.5 sm:px-[10px] sm:py-[7px]">
    <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-white/50 sm:gap-[6px] sm:text-[10px]">
      <i className={classNames("fas", icon, "text-[0.7rem] sm:text-[9px]")} />
      {label}
    </span>
    {action}
  </div>
)

const Skeleton: React.FC<{ className: string }> = ({ className }) => (
  <span className={classNames("block animate-pulse rounded bg-white/10", className)} />
)

// Who you are and where you sit on the ladder, shown beside the mode menu so
// the reason to play another game is on screen while you pick one.
const LobbyPlayerPanel: React.FC<{
  // The lobby owns where this column sits in its grid; the panel only owns
  // what it looks like.
  className?: string
}> = ({ className }) => {
  const account = useAppSelector((state) => state.global.userData)
  const email = getUserCreds().email
  const [stats, setStats] = useState<IUserLifetimeStatsResponse | null>(null)
  const [ladder, setLadder] = useState<ILeaderboardEntry[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    const loadStats = async () => {
      if (!email) return
      try {
        const response = await fetch(`${host}/api/stats/user`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        })
        if (!response.ok) return
        const data = await response.json()
        if (!cancelled) setStats(data)
      } catch {
        // This column is supporting detail on a page whose job is starting a
        // game — a failed fetch leaves its empty state up and nothing else
        // about the lobby breaks.
      }
    }

    const loadLadder = async () => {
      try {
        const response = await fetch(
          `${host}/api/stats/leaderboard?limit=${LADDER_SIZE}`
        )
        if (!response.ok) return
        const data = await response.json()
        if (!cancelled) setLadder(data.leaderboard || [])
      } catch {
        // Same here: no ladder simply means no ladder.
      }
    }

    setLoading(true)
    Promise.all([loadStats(), loadLadder()]).finally(() => {
      if (!cancelled) setLoading(false)
    })

    return () => {
      cancelled = true
    }
  }, [email])

  const rating = stats?.rating ?? 0
  const tier = getRankTier(rating)
  const nextTier = getNextTier(rating)
  // How far through the current band the rating sits. The top band has nothing
  // left to climb to, so it simply reads as full.
  const tierProgress = nextTier
    ? clampScore(((rating - tier.min) / (nextTier.min - tier.min)) * 100)
    : 100

  const gamesPlayed = stats?.gamesPlayed ?? 0
  const isRanked = gamesPlayed > 0
  const recentGames = stats?.recentGames ?? []
  const streak = stats?.currentWinStreak
    ? { label: `${stats.currentWinStreak} win streak`, won: true }
    : stats?.currentLossStreak
      ? { label: `${stats.currentLossStreak} loss streak`, won: false }
      : null

  const username = account?.username || stats?.username || "Player"
  const profileImage = account?.profileImage || avatar
  const myLadderIndex = ladder.findIndex((entry) => entry.email === email)

  return (
    <div
      className={classNames(
        "flex w-[34rem] max-w-[90vw] flex-col gap-4 sm:h-full sm:min-h-0 sm:w-auto sm:min-w-0 sm:max-w-none sm:flex-1 sm:gap-[8px] xl:h-full xl:min-h-0 xl:w-auto",
        className
      )}
    >
      {/* Your standing. */}
      <Card className="shrink-0">
        <div className="flex items-center gap-4 px-5 py-4 sm:gap-[10px] sm:px-[10px] sm:py-[8px]">
          <img
            src={profileImage}
            alt={username}
            className="h-14 w-14 flex-shrink-0 rounded-full object-cover sm:h-[34px] sm:w-[34px]"
            // The ring takes the tier's colour, so the badge and the portrait
            // agree at a glance. Inline because the colour comes from data.
            style={{
              boxShadow: `0 0 0 2px ${isRanked ? tier.color : "rgba(255,255,255,0.2)"}, 0 0 22px -4px ${tier.glow}`,
            }}
          />
          <div className="min-w-0 flex-1">
            <div className="truncate font-fourth text-lg font-bold leading-tight text-white sm:text-[14px]">
              {username}
            </div>
            {loading ? (
              <Skeleton className="mt-1.5 h-3 w-24" />
            ) : (
              <div className="mt-0.5 flex flex-wrap items-center gap-2 sm:gap-[6px]">
                <span
                  className="flex items-center gap-1.5 text-sm font-semibold leading-tight sm:text-[11px]"
                  style={{
                    color: isRanked ? tier.color : "rgba(255,255,255,0.4)",
                  }}
                >
                  <i
                    className={classNames(
                      "fas",
                      isRanked ? tier.icon : "fa-circle-dot",
                      "text-xs sm:text-[10px]"
                    )}
                  />
                  {isRanked ? tier.name : "Unranked"}
                </span>
                {/* A run either way is the thing most likely to decide whether
                    you queue again, so it rides next to the tier. */}
                {streak && (
                  <span
                    className={classNames(
                      "rounded-full px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wider sm:text-[9px]",
                      streak.won
                        ? "bg-emerald-500/20 text-emerald-200"
                        : "bg-rose-500/20 text-rose-200"
                    )}
                  >
                    {streak.label}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex-shrink-0 text-right">
            <div className="text-[0.65rem] font-semibold uppercase tracking-wider text-white/40 sm:text-[9px]">
              Rating
            </div>
            {loading ? (
              <Skeleton className="ml-auto mt-1 h-6 w-14" />
            ) : (
              <div
                className="font-fourth text-2xl font-bold leading-none tabular-nums sm:text-[19px]"
                style={{ color: isRanked ? tier.color : "#ffffff" }}
              >
                {formatNumber(rating)}
              </div>
            )}
          </div>
        </div>

        {/* Climb to the next tier. Dropped on a phone, where the column
            has no height to spend on a bar that repeats what the rating and
            tier beside it already say. */}
        <div className="px-5 pb-4 sm:hidden xl:block xl:pb-3">
          <div className="mb-1.5 flex items-center justify-between text-[0.65rem] uppercase tracking-wider">
            <span style={{ color: tier.color }}>{tier.name}</span>
            <span className="text-white/40">
              {nextTier
                ? `${formatNumber(Math.max(0, nextTier.min - rating))} to ${nextTier.name}`
                : "Top tier reached"}
            </span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-white/5">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{
                width: `${loading ? 0 : tierProgress}%`,
                background: `linear-gradient(90deg, ${tier.color}88, ${tier.color})`,
              }}
            />
          </div>
        </div>

        {/* The three numbers worth a glance before heading back in. */}
        <div className="grid grid-cols-3 border-t border-white/10">
          {[
            { label: "Played", value: formatNumber(gamesPlayed), accent: "#ffffff" },
            { label: "Won", value: formatNumber(stats?.gamesWon ?? 0), accent: "#ffffff" },
            {
              label: "Win Rate",
              value: formatPercent(stats?.derived?.winRate ?? 0, 0),
              accent: "#6EE7B7",
            },
          ].map((stat, index) => (
            <div
              key={stat.label}
              className={classNames(
                "px-2 py-3 text-center sm:py-[6px]",
                index > 0 && "border-l border-white/10"
              )}
            >
              {loading ? (
                <Skeleton className="mx-auto h-5 w-10" />
              ) : (
                <div
                  className="font-fourth text-lg font-bold leading-none tabular-nums sm:text-[15px]"
                  style={{ color: stat.accent }}
                >
                  {stat.value}
                </div>
              )}
              <div className="mt-1 text-[0.65rem] uppercase tracking-wider text-white/40 sm:mt-[2px] sm:text-[9px]">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* How the last few games went. This is the block that takes the slack in
          the column: it is the one list here that keeps growing with play, so
          giving the spare height to the ladder instead would leave a tall card
          holding four short rows. */}
      <Card className="sm:min-h-0 sm:flex-1 xl:min-h-0 xl:flex-1">
        <CardHeading
          label="Your Recent Games"
          icon="fa-clock-rotate-left"
          action={
            <Link
              to="/stats"
              className="flex items-center gap-1.5 text-xs font-semibold text-amber-300/70 transition-colors hover:text-amber-200 sm:text-[10px]"
            >
              All stats
              <i className="fas fa-arrow-right text-[0.6rem]" />
            </Link>
          }
        />

        <div className="flex max-h-[22rem] flex-col overflow-y-auto p-2 sm:max-h-none sm:min-h-0 sm:flex-1 sm:p-[4px] xl:max-h-none xl:min-h-0 xl:flex-1">
          {loading ? (
            <div className="flex flex-col gap-2 p-1">
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton key={index} className="h-11 w-full" />
              ))}
            </div>
          ) : recentGames.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-8 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/5 ring-1 ring-white/10">
                <i className="fas fa-dice text-lg text-white/30" />
              </span>
              <p className="text-base font-semibold text-white/70 sm:text-[13px]">
                No games on record
              </p>
              <p className="text-sm leading-snug text-white/40 sm:text-[11px]">
                Finish one and your result, placement and rating change land
                here.
              </p>
            </div>
          ) : (
            recentGames.map((game) => (
              <div
                key={game.gameId}
                className="flex items-center gap-3 rounded-xl px-3 py-2 transition-colors hover:bg-white/5 sm:gap-[8px] sm:px-[6px] sm:py-[4px]"
              >
                <span
                  className={classNames(
                    "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-sm font-bold sm:h-[24px] sm:w-[24px] sm:text-[12px]",
                    game.won
                      ? "bg-emerald-500/20 text-emerald-200 ring-1 ring-emerald-400/30"
                      : "bg-white/5 text-white/40 ring-1 ring-white/10"
                  )}
                >
                  {game.won ? "W" : "L"}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-white/80 sm:text-[12px]">
                    {ordinal(game.placement)} of {game.playerCount}
                    {game.wentBankrupt && (
                      <span className="ml-2 text-[0.65rem] font-semibold uppercase tracking-wider text-rose-300/70 sm:text-[9px]">
                        bankrupt
                      </span>
                    )}
                  </span>
                  <span className="block truncate text-[0.65rem] text-white/30 sm:text-[10px]">
                    {timeAgo(game.playedAt)} · {game.turns} turns
                  </span>
                </span>
                <span
                  className={classNames(
                    "flex-shrink-0 font-fourth text-sm font-bold tabular-nums sm:text-[12px]",
                    game.ratingChange >= 0 ? "text-emerald-300" : "text-rose-300"
                  )}
                  title="Rating change from this game"
                >
                  {game.ratingChange >= 0 ? "+" : "−"}
                  {Math.abs(Math.round(game.ratingChange))}
                </span>
              </div>
            ))
          )}
        </div>
      </Card>

      {/* Who to beat. Content-sized: the ladder is only ever a handful of rows,
          so it sits at the foot of the column rather than stretching.

          Not on a phone: with the card above and your own recent games there is
          room for one list in this column, and the one that answers "should I
          play another" is your own. The ladder returns on a short desktop
          window, where the column is tall enough for both. */}
      <Card className="shrink-0 sm:hidden xl:flex">
        <CardHeading label="Top Players" icon="fa-ranking-star" />

        <div className="flex flex-col p-2">
          {loading ? (
            <div className="flex flex-col gap-2 p-1">
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} className="h-9 w-full" />
              ))}
            </div>
          ) : ladder.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-8 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/5 ring-1 ring-white/10">
                <i className="fas fa-ranking-star text-lg text-white/30" />
              </span>
              <p className="text-base font-semibold text-white/70">
                The ladder is empty
              </p>
              <p className="text-sm leading-snug text-white/40">
                Nobody has finished a game yet. Win one and the top spot is
                yours.
              </p>
            </div>
          ) : (
            ladder.map((entry, index) => {
              const entryTier = getRankTier(entry.rating)
              const isMe = entry.email === email
              return (
                <div
                  key={entry.email}
                  className={classNames(
                    "flex items-center gap-3 rounded-xl px-3 py-2 transition-colors sm:py-1.5",
                    isMe ? "bg-amber-500/10 ring-1 ring-amber-400/30" : "hover:bg-white/5"
                  )}
                >
                  <span
                    className="w-5 flex-shrink-0 text-center font-fourth text-sm font-bold tabular-nums"
                    style={{ color: medalColor(index) }}
                  >
                    {index + 1}
                  </span>
                  <i
                    className={classNames("fas", entryTier.icon, "w-4 flex-shrink-0 text-center text-xs")}
                    style={{ color: entryTier.color }}
                    title={entryTier.name}
                  />
                  <span className="min-w-0 flex-1 truncate text-sm font-semibold text-white/80">
                    {entry.username || entry.email}
                    {isMe && (
                      <span className="ml-2 rounded bg-amber-500/20 px-1.5 py-0.5 text-[0.6rem] uppercase tracking-wider text-amber-200">
                        you
                      </span>
                    )}
                  </span>
                  <span className="flex-shrink-0 text-xs text-white/30 tabular-nums">
                    {formatNumber(entry.gamesWon)}W
                  </span>
                  <span className="w-10 flex-shrink-0 text-right font-fourth text-sm font-bold tabular-nums text-amber-200">
                    {formatNumber(entry.rating)}
                  </span>
                </div>
              )
            })
          )}
        </div>

        {/* Off the board: say where they actually stand rather than leaving
            them to work it out from a list they are not on. */}
        {!loading && ladder.length > 0 && myLadderIndex === -1 && (
          <div className="border-t border-white/10 px-5 py-2.5 text-xs text-white/40">
            {isRanked ? (
              <>
                You are outside the top {ladder.length} at{" "}
                <span className="font-semibold text-amber-200">
                  {formatNumber(rating)}
                </span>
                .
              </>
            ) : (
              <>Finish a game to claim a place on the ladder.</>
            )}
          </div>
        )}
      </Card>
    </div>
  )
}

export default LobbyPlayerPanel
